import React, { useState, useEffect } from 'react';
import './SplitterApp.css';
import YouTubeTranscriptCard from './YouTubeTranscript/YouTubeTranscriptCard';
import SplitterHelperModal from './SplitterHelperModal';

function SplitterApp() {
  const [inputText, setInputText] = useState('');
  const [chunks, setChunks] = useState([]);
  const [chunkSize, setChunkSize] = useState(50000);
  const [copiedChunkIndex, setCopiedChunkIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copyAllStatus, setCopyAllStatus] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showHelper, setShowHelper] = useState(false);


  useEffect(() => {
    document.title = "ProdPilot - Splitter";
  }, []);

  const splitIntoChunks = (text, maxSize) => {
    const chunks = [];
    for (let i = 0; i < text.length; i += maxSize) {
      chunks.push(text.slice(i, i + maxSize));
    }
    return chunks;
  };

  const getDefaultInstructions = (totalChunks) => `
The content will be sent in multiple parts, labeled as follows:
[START PART X/Y]
Content of part X of Y
[END PART X/Y]
Please acknowledge each part until you see "ALL PARTS SENT".
Total parts: ${totalChunks}.
  `.trim();

  const getYoutubeInstructions = (totalChunks) => `
Objective: Break down the content of a podcast interview or video.
Submission Format:
If sent in parts, each will be labeled as [START PART X/Y] and [END PART X/Y].
Acknowledge each part until "ALL PARTS SENT" is received.
Deliverables:
  - A very detailed summary that highlights key insights, best parts, actionable points, and recurring themes.
  - A breakdown of the most useful sections of the content.
  - Suggestions for social media clips, including time stamps and titles.
Total parts: ${totalChunks}.
  `.trim();

  const handleSplitText = (type = 'default') => {
    setLoading(true);
    setTimeout(() => {
      const contentChunks = splitIntoChunks(inputText, chunkSize);
      const instructionsText =
        type === 'youtube'
          ? getYoutubeInstructions(contentChunks.length + 1)
          : getDefaultInstructions(contentChunks.length + 1);
      const instructionChunks = splitIntoChunks(instructionsText, chunkSize);
      const combinedChunks = [...instructionChunks, ...contentChunks];
      setChunks(formatChunks(combinedChunks));
      setLoading(false);
      setCopiedChunkIndex(null);
      setCopyAllStatus(false);
      setSummary('');
    }, 500);
  };

  const formatChunks = (chunks) => {
    const allPartsSentMessage = "ALL PARTS SENT";
    if ((chunks[chunks.length - 1] + "\n\n" + allPartsSentMessage).length > chunkSize) {
      chunks.push(allPartsSentMessage);
    } else {
      chunks[chunks.length - 1] += "\n\n" + allPartsSentMessage;
    }
    return chunks.map((chunk, index) =>
      `[START PART ${index + 1}/${chunks.length}]\n${chunk}\n[END PART ${index + 1}/${chunks.length}]`
    );
  };

  const handleCopy = (index) => {
    navigator.clipboard.writeText(chunks[index]).then(() => {
      setCopiedChunkIndex(index);
    });
  };

  const handleCopyAll = () => {
    const allText = chunks.join('\n\n');
    navigator.clipboard.writeText(allText).then(() => {
      setCopyAllStatus(true);
    });
  };

  const handleClear = () => {
    setInputText('');
    setChunks([]);
    setCopiedChunkIndex(null);
    setCopyAllStatus(false);
    setSummary('');
  };

  const getSummary = async (text) => {
    const apiKey = process.env.REACT_APP_HUGGINGFACE_API_KEY;
    if (!apiKey) {
      throw new Error("No API key provided. Please set REACT_APP_HUGGINGFACE_API_KEY.");
    }

    const endpoint = "https://api-inference.huggingface.co/models/facebook/bart-large-cnn";
    const safeCharLimit = 1500;
    const textToSummarize = text.length > safeCharLimit ? text.slice(0, safeCharLimit) : text;

    const payload = {
      inputs: textToSummarize,
      parameters: {
        min_length: 200,
        max_length: 400,
        do_sample: false
      }
    };

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to generate summary.");
    }

    const result = await response.json();
    return result[0]?.summary_text || "Summary could not be generated.";
  };

  const handleSummary = async () => {
    if (inputText.trim() === "") return;

    setSummaryLoading(true);
    setSummary('');
    try {
      const summaryChunks = splitIntoChunks(inputText, 6500);
      const allSummaries = [];

      for (let i = 0; i < summaryChunks.length; i++) {
        setSummary(`Summarizing chunk ${i + 1} of ${summaryChunks.length}...`);
        const result = await getSummary(summaryChunks[i]);
        allSummaries.push(`Chunk ${i + 1} Summary:\n${result}`);
        await new Promise(resolve => setTimeout(resolve, 30));
      }

      setSummary(allSummaries.join('\n\n'));
    } catch (err) {
      setSummary("Error generating summary: " + err.message);
    }

    setSummaryLoading(false);
  };

  return (
    <div className="splitter-container">

      {/* 👆 Chunk controls remain at the very top */}
      {chunks.length > 0 && (
        <div className="chunk-list">
          {chunks.map((_, index) => (
            <button
              key={index}
              onClick={() => handleCopy(index)}
              className={`chunk-button ${copiedChunkIndex === index ? 'copied' : ''}`}
            >
              Copy Chunk {index + 1}
            </button>
          ))}
          <button onClick={handleCopyAll} disabled={copyAllStatus} className="copy-all-button">
            {copyAllStatus ? 'All Copied!' : 'Copy All'}
          </button>
        </div>
      )}

      {/* 👇 Main Splitter Section */}
      <div className="text-splitter">
        <h1>Splitter</h1>
        <textarea
          rows="10"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Paste your text here..."
          className="splitter-textarea"
        />
        <div className="char-counter">{inputText.length} characters</div>

        <div className="control-panel">
          <label>
            Chunk Size:
            <input
              type="number"
              min="1000"
              max="50000"
              value={chunkSize}
              onChange={(e) => setChunkSize(Number(e.target.value))}
              className="chunk-size-input"
            />
          </label>
          <div className="button-group">
            <button
              onClick={() => handleSplitText('default')}
              disabled={!inputText || loading}
              className="control-button"
            >
              Split Text
            </button>
            <button
              onClick={() => handleSplitText('youtube')}
              disabled={!inputText || loading}
              className="control-button"
            >
              Split Text with YouTube Prompt
            </button>
            <button
              onClick={handleSummary}
              disabled={!inputText || loading || summaryLoading}
              className="control-button"
            >
              {summaryLoading ? 'Summarizing...' : 'Summary'}
            </button>
          </div>
          <button
            onClick={handleClear}
            disabled={!inputText}
            className="control-button"
          >
            Clear Text
          </button>
          <button
  onClick={() => setShowHelper(true)}
  className="control-button"
>
   Help
</button>
{showHelper && <SplitterHelperModal onClose={() => setShowHelper(false)} />}

        </div>
        
      </div>

      {/* 👇 Summary Result Section */}
      {summary && (
        <div className="summary-card">
          <h2>Summary</h2>
          <textarea
            readOnly
            value={summary}
            className="combined-text"
            placeholder="Summary will appear here..."
          />
          <p>You can copy the summary for further use.</p>
        </div>
      )}

      {/* 👇 YouTube Transcript Section at Bottom */}
      <YouTubeTranscriptCard onPasteToSplitter={setInputText} />
    </div>
  );
}

export default SplitterApp;
